/**
 * Logger Utility - Following patterns from main B18 app
 * Simplified version for plugin (no Redux)
 * Only sends ERROR logs to backend
 */

import { _ENV_VARIABLES } from '@/utils/constants'
import { getAuth } from 'firebase/auth'

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

export interface LogContext {
  userId?: string
  email?: string
  component?: string
  action?: string
  metadata?: Record<string, any>
  [key: string]: any
}

export interface LogEntry {
  level: LogLevel
  message: string
  timestamp: string
  context?: LogContext
  error?: Error
  stack?: string
}

const SENSITIVE_KEYS = new Set([
  'password',
  'token',
  'apiKey',
  'secret',
  'ssn',
  'creditCard'
])

// Walk what JSON.stringify walks by own keys. Objects with their own toJSON
// (Date, AxiosError) and boxed primitives keep their JSON form.
const isWalkable = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { toJSON?: unknown }).toJSON !== 'function' &&
  !(
    value instanceof String ||
    value instanceof Number ||
    value instanceof Boolean
  )

const maskEmail = (email: string): string => {
  const at = email.lastIndexOf('@')
  return at === -1 ? '***' : `***@${email.slice(at + 1)}`
}

const sanitizeEntry = (
  key: string,
  value: unknown,
  ancestors: Set<object>
): unknown => {
  // Redact whenever the key is PRESENT: a falsy value ('' / 0 / false) is still the shape of a credential.
  if (SENSITIVE_KEYS.has(key)) return '[REDACTED]'
  if (key === 'email' && typeof value === 'string' && value) {
    return maskEmail(value)
  }
  if (
    key === 'userId' &&
    (typeof value === 'number' || (typeof value === 'string' && value))
  ) {
    return `****${String(value).slice(-4)}`
  }
  return sanitizeValue(value, ancestors)
}

const sanitizeObject = (
  value: Record<string, unknown>,
  ancestors: Set<object>
): Record<string, unknown> => {
  ancestors.add(value)
  const result = Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      sanitizeEntry(key, item, ancestors)
    ])
  )
  ancestors.delete(value)
  return result
}

const sanitizeValue = (value: unknown, ancestors: Set<object>): unknown => {
  if (!Array.isArray(value) && !isWalkable(value)) return value
  if (ancestors.has(value)) return '[Circular]'
  if (!Array.isArray(value)) return sanitizeObject(value, ancestors)
  ancestors.add(value)
  const result = value.map(item => sanitizeValue(item, ancestors))
  ancestors.delete(value)
  return result
}

class Logger {
  private isDevelopment: boolean
  private appName: string = 'Front-B18-Plugin'

  constructor() {
    this.isDevelopment = import.meta.env.DEV
  }

  /**
   * Sanitize sensitive data from logs, at any depth, without touching the
   * caller's object
   */
  private sanitizeContext(context?: LogContext): LogContext | undefined {
    if (!context) return undefined
    return sanitizeObject(context, new Set()) as LogContext
  }

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error
  ): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: this.sanitizeContext(context),
      error,
      stack: error?.stack
    }
  }

  /**
   * Send error log to backend API
   * Only sends ERROR logs in staging and production environments
   */
  private async sendToBackend(entry: LogEntry): Promise<void> {
    // Only send error logs in staging and production (not development)
    if (this.isDevelopment || entry.level !== LogLevel.ERROR) return

    try {
      const auth = getAuth()
      const token = await auth.currentUser?.getIdToken()

      if (!token) return

      const logPayload = {
        level: entry.level,
        message: entry.message,
        timestamp: entry.timestamp,
        context: entry.context,
        error: entry.error
          ? {
              name: entry.error.name,
              message: entry.error.message
            }
          : undefined,
        stack: entry.stack,
        environment: _ENV_VARIABLES.NODE_ENV as
          | 'development'
          | 'staging'
          | 'production',
        appName: this.appName,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        url: typeof window !== 'undefined' ? window.location.href : undefined
      }

      // Send directly to backend API
      await fetch(`${_ENV_VARIABLES.BACKEND_URL_PROD}/logging/createLog`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(logPayload)
      })
    } catch (_error) {
      // Silently fail - don't want logging errors to break the app
    }
  }

  private logToConsole(entry: LogEntry): void {
    const prefix = `[${this.appName}][${entry.level.toUpperCase()}]`
    const timestamp = new Date(entry.timestamp).toLocaleTimeString()
    const contextStr = entry.context ? JSON.stringify(entry.context) : ''

    switch (entry.level) {
      case LogLevel.DEBUG:
        if (this.isDevelopment) {
          console.debug(`${prefix} ${timestamp} - ${entry.message}`, contextStr)
        }
        break
      case LogLevel.INFO:
        console.info(`${prefix} ${timestamp} - ${entry.message}`, contextStr)
        break
      case LogLevel.WARN:
        console.warn(`${prefix} ${timestamp} - ${entry.message}`, contextStr)
        break
      case LogLevel.ERROR:
        console.error(
          `${prefix} ${timestamp} - ${entry.message}`,
          contextStr,
          entry.error
        )
        if (this.isDevelopment && entry.stack) {
          console.error(entry.stack)
        }
        break
    }
  }

  private log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error
  ): void {
    const entry = this.createLogEntry(level, message, context, error)

    this.logToConsole(entry)

    // Send error logs to backend (staging & production only)
    this.sendToBackend(entry)
  }

  /**
   * Debug logging - only shows in development
   */
  debug(message: string, context?: LogContext): void {
    this.log(LogLevel.DEBUG, message, context)
  }

  /**
   * Info logging - general application flow
   */
  info(message: string, context?: LogContext): void {
    this.log(LogLevel.INFO, message, context)
  }

  /**
   * Warning logging - potential issues
   */
  warn(message: string, context?: LogContext): void {
    this.log(LogLevel.WARN, message, context)
  }

  /**
   * Error logging - critical failures
   * Only ERROR logs are sent to backend in staging/production
   */
  error(message: string, error?: Error, context?: LogContext): void {
    this.log(LogLevel.ERROR, message, context, error)
  }
}

export const logger = new Logger()

export default Logger
